// ── Match Score Engine ─────────────────────────────────────────
// Pure TypeScript — no platform dependencies.
// Single source of truth for all scoring logic.

import type {
  PropertyData,
  BuyerCriteria,
  MatchBreakdown,
  MatchScoreResult,
  MatchLevel,
  ComponentResult,
} from './types.ts';
import { normalizeCityName, normalizeCityArray } from './cities.ts';

// ── Weight constants ──────────────────────────────────────────────────────────
export const WEIGHTS = {
  location: 25,
  budget: 25,
  rooms: 15,
  propertyType: 10,
  size: 10,
  amenities: 10,
  condition: 5,
} as const;

export const TOTAL_WEIGHT =
  WEIGHTS.location + WEIGHTS.budget + WEIGHTS.rooms + WEIGHTS.propertyType +
  WEIGHTS.size + WEIGHTS.amenities + WEIGHTS.condition; // 100

// ── Critical vs Secondary ─────────────────────────────────────────────────────
export const CRITICAL_CATEGORIES = ['budget', 'propertyType', 'rooms'] as const;
export const SECONDARY_CATEGORIES = ['location', 'size', 'amenities', 'condition'] as const;

// ── Visibility thresholds ─────────────────────────────────────────────────────
export const DEFAULT_MIN_SCORE = 60;
export const WEAK_TOGGLE_MIN_SCORE = 40;

// ── Level helpers ─────────────────────────────────────────────────────────────
export function getMatchLevel(score: number): MatchLevel {
  if (score >= 95) return 'perfect';
  if (score >= 85) return 'excellent';
  if (score >= 75) return 'strong';
  if (score >= 60) return 'potential';
  return 'weak';
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

// ── Component scorers ─────────────────────────────────────────────────────────

function scoreLocation(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  let cityScore = 0;
  let neighScore = 0;
  let matchedCity = false;
  let matchedNeigh = false;

  if (!criteria.cities || criteria.cities.length === 0) {
    cityScore = 15;
  } else {
    const propCity = normalizeCityName(property.city);
    const criteriaCities = normalizeCityArray(criteria.cities);
    if (propCity && criteriaCities.includes(propCity)) {
      cityScore = 15;
      matchedCity = true;
    }
  }

  if (!criteria.neighborhoods || criteria.neighborhoods.length === 0) {
    neighScore = 10;
  } else {
    const propNeigh = property.neighborhood;
    if (propNeigh && criteria.neighborhoods.includes(propNeigh)) {
      neighScore = 10;
      matchedNeigh = true;
    }
  }

  const total = cityScore + neighScore;
  const reason = matchedCity && matchedNeigh
    ? 'עיר ושכונה מתאימות'
    : matchedCity
      ? 'עיר מתאימה'
      : cityScore === 15
        ? 'עיר מתאימה (אין העדפת שכונה)'
        : 'עיר לא תואמה';

  return {
    score: total,
    matched: matchedCity && matchedNeigh,
    partial: matchedCity && !matchedNeigh && !!(criteria.neighborhoods && criteria.neighborhoods.length > 0),
    mismatch: !matchedCity && !!(criteria.cities && criteria.cities.length > 0),
    severe: false,
    reason,
  };
}

function scoreBudget(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  if (!criteria.priceMin && !criteria.priceMax) {
    return { score: 25, matched: true, partial: false, mismatch: false, severe: false, reason: 'אין העדפת תקציב' };
  }

  const price = property.price;
  if (price === undefined || price === null || isNaN(price)) {
    return { score: 12, matched: false, partial: false, mismatch: false, severe: false, reason: 'חסר מחיר נכס' };
  }

  const min = criteria.priceMin || 0;
  const max = criteria.priceMax || Infinity;

  if (price >= min && price <= max) {
    return { score: 25, matched: true, partial: false, mismatch: false, severe: false, reason: 'התקציב מתאים' };
  }

  // Above max
  if (price > max && max !== Infinity) {
    const overPct = ((price - max) / max) * 100;
    if (overPct <= 5) return { score: 20, matched: false, partial: true, mismatch: false, severe: false, reason: `מחיר ${overPct.toFixed(0)}% מעל התקציב` };
    if (overPct <= 10) return { score: 12, matched: false, partial: true, mismatch: false, severe: false, reason: `מחיר ${overPct.toFixed(0)}% מעל התקציב` };
    if (overPct <= 15) return { score: 5, matched: false, partial: false, mismatch: true, severe: true, reason: `מחיר ${overPct.toFixed(0)}% מעל התקציב` };
    return { score: 0, matched: false, partial: false, mismatch: true, severe: true, reason: `מחיר ${overPct.toFixed(0)}% מעל התקציב` };
  }

  // Below min — calibrated: steeper penalty for far-below
  if (price < min && min > 0) {
    const underPct = ((min - price) / min) * 100;
    if (underPct <= 10) return { score: 23, matched: false, partial: true, mismatch: false, severe: false, reason: `מחיר ${underPct.toFixed(0)}% מתחת למינימום` };
    if (underPct <= 20) return { score: 15, matched: false, partial: false, mismatch: true, severe: false, reason: `מחיר ${underPct.toFixed(0)}% מתחת למינימום` };
    if (underPct <= 30) return { score: 7, matched: false, partial: false, mismatch: true, severe: true, reason: `מחיר ${underPct.toFixed(0)}% מתחת למינימום` };
    return { score: 0, matched: false, partial: false, mismatch: true, severe: true, reason: `מחיר ${underPct.toFixed(0)}% מתחת למינימום` };
  }

  return { score: 25, matched: true, partial: false, mismatch: false, severe: false, reason: 'התקציב מתאים' };
}

function scoreRooms(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  if (!criteria.rooms || criteria.rooms.length === 0) {
    return { score: 15, matched: true, partial: false, mismatch: false, severe: false, reason: 'אין העדפת חדרים' };
  }

  const propRooms = property.rooms;
  if (propRooms === undefined || propRooms === null || isNaN(propRooms)) {
    return { score: 7, matched: false, partial: false, mismatch: false, severe: false, reason: 'חסר מספר חדרים' };
  }

  if (criteria.rooms.includes(propRooms)) {
    return { score: 15, matched: true, partial: false, mismatch: false, severe: false, reason: 'מספר חדרים מתאים' };
  }

  const minDiff = Math.min(...criteria.rooms.map(r => Math.abs(r - propRooms)));

  if (minDiff <= 0.5) {
    return { score: 11, matched: false, partial: true, mismatch: false, severe: false, reason: `חדרים — הפרש ${minDiff} מהמבוקש` };
  }
  if (minDiff <= 1) {
    return { score: 6, matched: false, partial: true, mismatch: false, severe: false, reason: `חדרים — הפרש ${minDiff} מהמבוקש` };
  }
  if (minDiff <= 2) {
    return { score: 3, matched: false, partial: false, mismatch: true, severe: true, reason: `חדרים — הפרש ${minDiff} מהמבוקש` };
  }
  return { score: 0, matched: false, partial: false, mismatch: true, severe: true, reason: `חדרים — הפרש ${minDiff} מהמבוקש` };
}

function scorePropertyType(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  if (!criteria.propertyTypes || criteria.propertyTypes.length === 0) {
    return { score: 10, matched: true, partial: false, mismatch: false, severe: false, reason: 'אין העדפת סוג נכס' };
  }

  const propType = property.propertyType;
  if (!propType) {
    return { score: 5, matched: false, partial: false, mismatch: false, severe: false, reason: 'חסר סוג נכס' };
  }

  if (criteria.propertyTypes.includes(propType)) {
    return { score: 10, matched: true, partial: false, mismatch: false, severe: false, reason: 'סוג נכס מתאים' };
  }
  return { score: 0, matched: false, partial: false, mismatch: true, severe: true, reason: 'סוג נכס לא תואם' };
}

function scoreSize(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  if (!criteria.minSize && !criteria.maxSize) {
    return { score: 10, matched: true, partial: false, mismatch: false, severe: false, reason: 'אין העדפת שטח' };
  }

  const size = property.size;
  if (size === undefined || size === null || isNaN(size)) {
    return { score: 5, matched: false, partial: false, mismatch: false, severe: false, reason: 'חסר שטח נכס' };
  }

  const min = criteria.minSize || 0;
  const max = criteria.maxSize || Infinity;

  if (size >= min && size <= max) {
    return { score: 10, matched: true, partial: false, mismatch: false, severe: false, reason: 'השטח בטווח המבוקש' };
  }

  if (size > max && max !== Infinity) {
    const overPct = ((size - max) / max) * 100;
    if (overPct <= 10) return { score: 6, matched: false, partial: true, mismatch: false, severe: false, reason: `שטח ${overPct.toFixed(0)}% מעל המקסימום` };
    if (overPct <= 20) return { score: 3, matched: false, partial: false, mismatch: true, severe: false, reason: `שטח ${overPct.toFixed(0)}% מעל המקסימום` };
    return { score: 0, matched: false, partial: false, mismatch: true, severe: false, reason: `שטח ${overPct.toFixed(0)}% מעל המקסימום` };
  }

  if (size < min && min > 0) {
    const underPct = ((min - size) / min) * 100;
    if (underPct <= 10) return { score: 6, matched: false, partial: true, mismatch: false, severe: false, reason: `שטח ${underPct.toFixed(0)}% מתחת למינימום` };
    if (underPct <= 20) return { score: 3, matched: false, partial: false, mismatch: true, severe: false, reason: `שטח ${underPct.toFixed(0)}% מתחת למינימום` };
    return { score: 0, matched: false, partial: false, mismatch: true, severe: false, reason: `שטח ${underPct.toFixed(0)}% מתחת למינימום` };
  }

  return { score: 10, matched: true, partial: false, mismatch: false, severe: false, reason: 'השטח בטווח המבוקש' };
}

function scoreAmenities(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  const requested: Array<{ key: string; label: string; has: boolean | undefined }> = [];

  if (criteria.parkingRequired) requested.push({ key: 'parking', label: 'חניה', has: property.hasParking });
  if (criteria.elevatorRequired) requested.push({ key: 'elevator', label: 'מעלית', has: property.hasElevator });
  if (criteria.saferoomRequired) requested.push({ key: 'saferoom', label: 'ממ"ד', has: property.hasSaferoom });
  if (criteria.balconyRequired) requested.push({ key: 'balcony', label: 'מרפסת', has: property.hasBalcony });

  if (requested.length === 0) {
    return { score: 10, matched: true, partial: false, mismatch: false, severe: false, reason: 'אין העדפות מאפיינים' };
  }

  const pointsPerAmenity = WEIGHTS.amenities / requested.length;
  let totalScore = 0;
  const matchedList: string[] = [];
  const missingList: string[] = [];

  for (const req of requested) {
    if (req.has === true) {
      totalScore += pointsPerAmenity;
      matchedList.push(req.label);
    } else {
      missingList.push(req.label);
    }
  }

  totalScore = Math.round(totalScore);
  const allMatched = missingList.length === 0;
  const reason = allMatched
    ? 'כל המאפיינים המבוקשים קיימים'
    : `חסר: ${missingList.join(', ')}`;

  return {
    score: totalScore,
    matched: allMatched,
    partial: missingList.length > 0 && matchedList.length > 0,
    mismatch: missingList.length > 0 && matchedList.length === 0,
    severe: false,
    reason,
  };
}

function scoreCondition(property: PropertyData, criteria: BuyerCriteria): ComponentResult {
  if (!criteria.conditionPreferences || criteria.conditionPreferences.length === 0) {
    return { score: 5, matched: true, partial: false, mismatch: false, severe: false, reason: 'אין העדפת מצב נכס' };
  }

  const propCondition = property.propertyCondition;
  if (!propCondition) {
    return { score: 3, matched: false, partial: false, mismatch: false, severe: false, reason: 'חסר מצב נכס' };
  }

  if (criteria.conditionPreferences.includes(propCondition)) {
    return { score: 5, matched: true, partial: false, mismatch: false, severe: false, reason: 'מצב הנכס מתאים' };
  }

  const compatiblePairs: Record<string, string[]> = {
    renovated_high: ['renovated', 'good'],
    renovated: ['renovated_high', 'good'],
    good: ['renovated', 'renovated_high', 'original'],
    original: ['good'],
  };

  if (compatiblePairs[propCondition]?.some(c => criteria.conditionPreferences!.includes(c))) {
    return { score: 2, matched: false, partial: true, mismatch: false, severe: false, reason: 'מצב נכס חלקית תואם' };
  }

  return { score: 0, matched: false, partial: false, mismatch: true, severe: false, reason: 'מצב נכס לא תואם' };
}

// ── Critical penalty multiplier ───────────────────────────────────────────────
function calculateCriticalMultiplier(
  bud: ComponentResult,
  pt: ComponentResult,
  rm: ComponentResult,
): number {
  let severeFailures = 0;
  if (bud.severe) severeFailures++;
  if (pt.severe) severeFailures++;
  if (rm.severe) severeFailures++;

  let mildFailures = 0;
  if (bud.mismatch && !bud.severe) mildFailures++;
  if (pt.mismatch && !pt.severe) mildFailures++;
  if (rm.mismatch && !rm.severe) mildFailures++;

  if (severeFailures === 0 && mildFailures === 0) return 1.0;
  if (severeFailures === 0 && mildFailures === 1) return 0.92;
  if (severeFailures === 0 && mildFailures === 2) return 0.82;

  if (severeFailures === 1 && mildFailures === 0) return 0.85;
  if (severeFailures === 1 && mildFailures === 1) return 0.75;
  if (severeFailures === 1 && mildFailures === 2) return 0.65;
  if (severeFailures === 2) return 0.55;
  if (severeFailures >= 3) return 0.40;

  return 1.0;
}

// ── Business sentence generator ───────────────────────────────────────────────
function generateBusinessSentence(
  bud: ComponentResult,
  loc: ComponentResult,
  rm: ComponentResult,
  pt: ComponentResult,
  sz: ComponentResult,
  am: ComponentResult,
  cd: ComponentResult,
  score: number,
): string {
  const parts: string[] = [];

  if (score >= 95) {
    return 'הנכס תואם כמעט לחלוטין את כל הדרישות המרכזיות של הקונה.';
  }

  const strengths: string[] = [];
  if (bud.matched) strengths.push('התקציב מתאים');
  if (loc.matched) strengths.push('המיקום מתאים');
  if (pt.matched) strengths.push('סוג הנכס מתאים');
  if (rm.matched) strengths.push('מספר החדרים מתאים');
  if (am.matched && am.reason !== 'אין העדפות מאפיינים') strengths.push('המאפיינים מתאימים');

  const weaknesses: string[] = [];
  if (bud.severe) weaknesses.push('מחיר הנכס רחוק מהתקציב');
  if (pt.mismatch) weaknesses.push('סוג הנכס שונה מהמבוקש');
  if (rm.severe) weaknesses.push('מספר החדרים שונה משמעותית');
  if (sz.mismatch) weaknesses.push('השטח אינו בטווח המבוקש');
  if (loc.mismatch) weaknesses.push('המיקום אינו תואם');

  if (strengths.length >= 3 && weaknesses.length === 0) {
    return 'הנכס תואם את רוב הדרישות המרכזיות של הקונה.';
  }

  if (strengths.length > 0 && weaknesses.length > 0) {
    return `הנכס ${strengths.slice(0, 2).join(' ו-')}, אך ${weaknesses.slice(0, 2).join(' ו-')}.`;
  }

  if (weaknesses.length > 0 && strengths.length === 0) {
    return `הנכס אינו תואם את הדרישות המרכזיות: ${weaknesses.join(', ')}.`;
  }

  if (strengths.length > 0) {
    return `הנכס ${strengths.join(', ')}.`;
  }

  return 'הנכס תואם חלקית את דרישות הקונה.';
}

// ── Hard match check ──────────────────────────────────────────────────────────
export function isHardMatchValid(property: PropertyData, _criteria: BuyerCriteria): boolean {
  if (!property) return false;
  if (!property.city && !property.propertyType && property.rooms === undefined) {
    return false;
  }
  return true;
}

// ── Main score function ───────────────────────────────────────────────────────
export function calculateMatchScore(
  property: PropertyData,
  criteria: BuyerCriteria,
): MatchScoreResult {
  const hardMatchPassed = isHardMatchValid(property, criteria);

  const loc = scoreLocation(property, criteria);
  const bud = scoreBudget(property, criteria);
  const rm = scoreRooms(property, criteria);
  const pt = scorePropertyType(property, criteria);
  const sz = scoreSize(property, criteria);
  const am = scoreAmenities(property, criteria);
  const cd = scoreCondition(property, criteria);

  const breakdown: MatchBreakdown = {
    location: loc.score,
    budget: bud.score,
    rooms: rm.score,
    propertyType: pt.score,
    size: sz.score,
    amenities: am.score,
    condition: cd.score,
  };

  const rawScore = clamp(
    Math.round(breakdown.location + breakdown.budget + breakdown.rooms + breakdown.propertyType + breakdown.size + breakdown.amenities + breakdown.condition),
    0,
    100,
  );

  const criticalMultiplier = calculateCriticalMultiplier(bud, pt, rm);
  const score = clamp(Math.round(rawScore * criticalMultiplier), 0, 100);
  const level = getMatchLevel(score);

  const matchedFields: string[] = [];
  const partialFields: string[] = [];
  const mismatchFields: string[] = [];
  const reasons: string[] = [];

  const components = [
    { name: 'מיקום', r: loc, key: 'location' as const },
    { name: 'תקציב', r: bud, key: 'budget' as const },
    { name: 'חדרים', r: rm, key: 'rooms' as const },
    { name: 'סוג נכס', r: pt, key: 'propertyType' as const },
    { name: 'שטח', r: sz, key: 'size' as const },
    { name: 'מאפיינים', r: am, key: 'amenities' as const },
    { name: 'מצב נכס', r: cd, key: 'condition' as const },
  ];

  const strengths: string[] = [];
  const weaknesses: string[] = [];

  for (const comp of components) {
    if (comp.r.matched) {
      matchedFields.push(comp.name);
      if (comp.r.reason !== 'אין העדפת תקציב' && comp.r.reason !== 'אין העדפת חדרים' && comp.r.reason !== 'אין העדפת סוג נכס' && comp.r.reason !== 'אין העדפות מאפיינים' && comp.r.reason !== 'אין העדפת שטח' && comp.r.reason !== 'אין העדפת מצב נכס') {
        strengths.push(comp.name);
      }
    } else if (comp.r.partial) {
      partialFields.push(comp.name);
    } else if (comp.r.mismatch) {
      mismatchFields.push(comp.name);
      weaknesses.push(comp.name);
    }
    if (comp.r.reason) {
      reasons.push(comp.r.reason);
    }
  }

  const businessSentence = generateBusinessSentence(bud, loc, rm, pt, sz, am, cd, score);

  return {
    score,
    rawScore,
    level,
    hardMatchPassed,
    criticalMultiplier,
    breakdown,
    matchedFields,
    partialFields,
    mismatchFields,
    reasons,
    businessSentence,
    strengths,
    weaknesses,
  };
}

// ── Seller → Buyer helper ─────────────────────────────────────────────────────
export function sellerCriteriaToPropertyData(
  sellerCriteria: BuyerCriteria & { propertyPrice?: number; propertyRooms?: number; propertySize?: number; propertyType?: string; propertyCity?: string; propertyNeighborhood?: string },
): PropertyData {
  return {
    price: sellerCriteria.propertyPrice,
    rooms: sellerCriteria.propertyRooms,
    size: sellerCriteria.propertySize,
    propertyType: sellerCriteria.propertyType || (sellerCriteria.propertyTypes && sellerCriteria.propertyTypes.length === 1 ? sellerCriteria.propertyTypes[0] : undefined),
    city: sellerCriteria.propertyCity || (sellerCriteria.cities && sellerCriteria.cities.length === 1 ? sellerCriteria.cities[0] : undefined),
    neighborhood: sellerCriteria.propertyNeighborhood || (sellerCriteria.neighborhoods && sellerCriteria.neighborhoods.length === 1 ? sellerCriteria.neighborhoods[0] : undefined),
  };
}

export function scoreBuyerAgainstSeller(
  buyerCriteria: BuyerCriteria,
  sellerProperty: PropertyData,
): MatchScoreResult {
  return calculateMatchScore(sellerProperty, buyerCriteria);
}
